import { useTheme } from '@mui/material';
import { Info } from '@mui/icons-material';
import { styled } from '@mui/system';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
  assertCapabilities,
  ws_manager,
} from 'Store/index';
import InputHandler from 'Components/InputHandler';
import { Container, Item } from 'Components/Grid';
import { ICapabilityContextProps, withCapabilities } from 'Containers/CapabilityHandler';
import { useNavigate } from 'react-router';
import Paper from 'Components/Paper';
import Divider from 'Components/Divider';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import CircularProgressLocal from 'Components/CircularProgress';
import { actions as actionsSession } from '../../../../store/session';
import PlatformCapabilities from '../../../../config/capabilities';
import Appointments from './Appointments';
import Columns from './Columns';
import { localStorageSave } from 'Utility/localStorage';
import { sortPlatformsByLocale } from './utils';
import { useConnections } from '../../../../net/ConnectionManagerReactAdapter';
import type { TCMReactState } from '../../../../net/ConnectionManagerReactAdapter';
import { createVisitAndModifyAuth } from 'Routes/_MainLander/fetchers';

const Section = styled('div')({
  paddingLeft: '3.5rem',
});

const optionFormatter = (s: string | number): React.JSX.Element => <FormattedMessage id={`general.opts.${s}`} />;
const optionFormatterPlatforms = (s: string | number): React.JSX.Element => (
  <FormattedMessage id={`header.platforms.${s}`} />
);

const SectionHeader = ({ text }: { text: string }): React.JSX.Element => {
  const theme = useTheme();
  return (
    <>
      <Container style={{ marginBottom: '0.5rem' }} alignItems="center">
        <Item style={{ width: '3.5rem' }}>
          <Info color="primary" style={{ display: 'block', fontSize: '2.6rem' }} />
        </Item>
        <Item>
          <div className="subtitle" style={{ color: theme.palette.grey[700] }}>
            <FormattedMessage id={text} />
          </div>
        </Item>
      </Container>
    </>
  );
};

const SmallText = ({ text }: { text: string }): React.JSX.Element => {
  const theme = useTheme();
  return (
    <span className="small" style={{ color: theme.palette.grey[500] }}>
      <FormattedMessage id={text} />
    </span>
  );
};

const VisitReason = ({ patient, capabilityGroups }: IOwnProps): React.JSX.Element | null => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const [reason, setReason] = React.useState('');
  const [selectedPlatform, setSelectedPlatform] = React.useState<Platform | null>(null);
  const [selectedOtherPlatform, setSelectedOtherPlatform] = React.useState<Platform | null>(null);
  const [selectedAppointment, setSelectedAppointment] = React.useState<string | null>(null);
  const [loadingPreferredPlatforms, setLoadingPreferredPlatforms] = React.useState<boolean>(true);
  const [preferredPlatforms, setPreferredPlatforms] = React.useState<Platform[] | null>(null);
  const connections: TCMReactState = useConnections();

  const allPlatforms = useSelector((s: IState) => s.session.data?.platforms) as Platform[] | undefined;
  const userSelectedPlatforms = useSelector(s => s.settings.userSettings.userSelectedPlatforms);
  const platformsNonPreferred = sortPlatformsByLocale(
    allPlatforms?.filter(
      p =>
        !preferredPlatforms?.includes(p) &&
        (!userSelectedPlatforms || userSelectedPlatforms.length === 0 || userSelectedPlatforms.includes(p)),
    ),
    fm,
  );

  const userid = useSelector((state: IState) => state.session.data?.useruuid);

  const dispatch = useDispatch();
  React.useEffect(() => {
    // Wait for user id and capability groups to be to be loaded so that lc capability can be
    // properly checked. This fixes a race condition between this component's mount and the session/capability requests.
    // User id functions as a surrogate for session load, capability groups default to empty object
    if (userid === undefined || capabilityGroups === undefined) return;

    const lifeCareSyncCapability = assertCapabilities([PlatformCapabilities.LIFECARE_DIAGNOSIS_SYNC], capabilityGroups);

    actionsSession
      .preferredPlatforms(dispatch, lifeCareSyncCapability)
      .then(platforms => {
        const filteredAvailable = platforms && platforms.filter(p => (allPlatforms ?? []).includes(p));
        setPreferredPlatforms(sortPlatformsByLocale(filteredAvailable ?? undefined, fm) ?? null);
        setLoadingPreferredPlatforms(false);
      })
      .catch(() => {
        setPreferredPlatforms(null);
        setLoadingPreferredPlatforms(false);
      });
  }, [userid, capabilityGroups]);

  // if there is exactly one preferred platform, select it automatically
  React.useEffect(() => {
    if (Array.isArray(preferredPlatforms) && preferredPlatforms.length === 1 && preferredPlatforms[0]) {
      setSelectedPlatform(preferredPlatforms[0]);
    } else {
      setSelectedPlatform(null);
    }
  }, [preferredPlatforms]);

  const changePlatform = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    setSelectedPlatform(value as Platform);
    setSelectedOtherPlatform(null);
  };
  const changeOtherPlatform = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    setSelectedOtherPlatform(value as Platform);
    setSelectedPlatform(null);
  };
  const changeReason = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    setReason(value as string);
  };
  const changeAppointment = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    setSelectedAppointment(value as string);
  };

  const history = useNavigate();

  const updateSession = (): void => {
    dispatch(actionsSession.updateSession());
  };
  const updatePlatforms = (): void => {
    if (selectedPlatform || selectedOtherPlatform) {
      localStorageSave('platform', (selectedPlatform ?? selectedOtherPlatform) as Platform);
      dispatch(actionsSession.setPlatform((selectedPlatform ?? selectedOtherPlatform) as Platform));
    }
  };

  const onClickNext = async (): Promise<void> => {
    if ((reason && reason !== '') || selectedAppointment) {
      await createVisitAndModifyAuth(reason, selectedAppointment, selectedPlatform ?? selectedOtherPlatform);
      updateSession();
      updatePlatforms();
      history('/');
    }
  };

  const unselectPatient = () => {
    void actionsSession.unselectPatientAndVisit(dispatch).then(res => {
      if (res) ws_manager.disconnect();
    });
  };

  const submitDisabled =
    (!selectedPlatform && !selectedOtherPlatform) ||
    (!reason && !selectedAppointment) ||
    connections.inflight_fetch_count_auth > 0;

  const appointmentsCapability = assertCapabilities([PlatformCapabilities.APPOINTMENTS], capabilityGroups);
  return (
    <Paper
      header={
        <>
          <span>{patient.patientlastnames + ' ' + patient.patientfirstnames}</span>
          <span style={{ marginLeft: '1rem', fontWeight: 400, fontSize: '1.6rem' }}>{patient.patientssn}</span>
        </>
      }
      style={{
        width: '70rem',
      }}
    >
      <SectionHeader text={'general.choosePlatform'} />
      <Section>
        <Columns
          leftItem={
            <>
              <div style={{ marginBottom: '0.5rem' }}>
                <SmallText text={'general.autoChosenPlatforms'} />
              </div>
              {loadingPreferredPlatforms ? (
                <Container>
                  <Item style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}>
                    <CircularProgressLocal size={20} />
                  </Item>
                  <Item style={{ fontSize: '1.6rem' }}>
                    <FormattedMessage id="general.loadingChosenPlatforms" />
                  </Item>
                </Container>
              ) : preferredPlatforms && preferredPlatforms.length > 0 ? (
                <InputHandler
                  type="Radio"
                  name="selectedPlatform"
                  editing={true}
                  options={preferredPlatforms}
                  optionFormatter={optionFormatterPlatforms}
                  formData={{
                    onChange: changePlatform,
                    document: { selectedPlatform },
                  }}
                  compact
                />
              ) : (
                <div style={{ fontSize: '1.6rem' }}>
                  <FormattedMessage id="general.noChosenPlatforms" />
                </div>
              )}
            </>
          }
          rightItem={
            <>
              <SmallText text={'general.otherPlatforms'} />
              <InputHandler
                type="Select"
                name="selectedOtherPlatform"
                editing={true}
                options={platformsNonPreferred}
                optionFormatter={optionFormatterPlatforms}
                formData={{
                  onChange: changeOtherPlatform,
                  document: { selectedOtherPlatform },
                }}
                placeholder="general.otherPlatformsPlaceholder"
              />
            </>
          }
        />
      </Section>
      <Divider />
      <SectionHeader text={'general.visitReason'} />
      <Section>
        <Columns
          leftItem={
            <InputHandler
              type="Radio"
              name="visitReason"
              editing={true}
              options={[
                'patientVisit',
                'patientPhone',
                'doctorOtherHospital',
                'doctorSameHospital',
                'retrospectiveDataTransfer',
                'other',
              ]}
              optionFormatter={optionFormatter}
              formData={{
                onChange: changeReason,
                document: { visitReason: reason },
              }}
              compact
            />
          }
          rightItem={<></>}
        />
      </Section>
      {appointmentsCapability && (
        <>
          <Divider />
          <SectionHeader text="appointments.title" />
          <Section>
            <Appointments selectedAppointment={selectedAppointment} changeAppointment={changeAppointment} />
          </Section>
        </>
      )}

      <Divider />
      <Container>
        <Item xs={6}>
          <ActionButtonRounded width={10} height={4} fontSize={16} text="general.cancel" onClick={unselectPatient} />
        </Item>
        <Item xs={6}>
          <Container justifyContent="flex-end">
            <Item>
              <ActionButtonRounded
                width={20}
                height={4}
                fontSize={16}
                text="general.toData"
                onClick={onClickNext}
                disabled={submitDisabled}
                disabledTooltip={<FormattedMessage id="general.visitSubmitDisabledNoReason" />}
                filled
              />
            </Item>
          </Container>
        </Item>
      </Container>
    </Paper>
  );
};

interface IOwnProps extends ICapabilityContextProps {
  patient: { patientid: string; patientfirstnames?: string; patientlastnames?: string; patientssn?: string };
}

export default withCapabilities(VisitReason);
